import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useMemo } from 'react';

import type { ClassSearchDetails } from '~entities/classes/modal/class-search';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Calendar } from '~shared/shadcn/ui/calendar';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

import {
  getAllowedClassDays,
  getAllowedDayIndexes,
  isBookablePrivateLessonDate,
  parseOfferingDate,
  toDateKey,
} from './private-lesson-booking-helpers';

type PrivateLessonDatePickerProps = {
  offering: ClassSearchDetails | null;
  date: string;
  onDateChange: (date: string) => void;
  label?: string;
};

export const PrivateLessonDatePicker = ({
  offering,
  date,
  onDateChange,
  label = 'Date',
}: PrivateLessonDatePickerProps) => {
  const allowedDays = useMemo(() => getAllowedClassDays(offering), [offering]);
  const allowedDayIndexes = useMemo(
    () => getAllowedDayIndexes(offering),
    [offering],
  );
  const windowStart = useMemo(
    () => parseOfferingDate(offering?.startDate),
    [offering?.startDate],
  );
  const windowEnd = useMemo(
    () => parseOfferingDate(offering?.endDate),
    [offering?.endDate],
  );
  const defaultMonth = useMemo(() => {
    const parsed = date ? new Date(`${date}T12:00:00`) : new Date();
    if (windowStart && windowStart > parsed) {
      return windowStart;
    }
    return parsed;
  }, [date, windowStart]);

  const selectedDate = date ? new Date(`${date}T12:00:00`) : undefined;

  return (
    <div className='space-y-2'>
      <Label>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            className={cn(
              'w-full justify-start text-left font-normal',
              !date && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className='mr-2 size-4' />
            {selectedDate
              ? format(selectedDate, 'MMMM d, yyyy')
              : 'Pick a class day'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            selected={selectedDate}
            defaultMonth={defaultMonth}
            onSelect={(nextDate) => {
              if (!nextDate) return;
              onDateChange(toDateKey(nextDate));
            }}
            disabled={(candidate) =>
              !isBookablePrivateLessonDate(
                candidate,
                offering,
                allowedDayIndexes,
                windowStart,
                windowEnd,
              )
            }
          />
        </PopoverContent>
      </Popover>
      {allowedDays.length > 0 && (
        <p className='text-xs text-muted-foreground'>
          Available on: {allowedDays.join(', ')}
        </p>
      )}
    </div>
  );
};
